import { BrowserModule } from '@angular/platform-browser';
import { NgModule, Injector, PLATFORM_ID, ApplicationRef, TransferState, DoBootstrap } from '@angular/core';
import { Location } from '@angular/common';
import { LandingPageModule } from '../../../../src/app/shared/landing-page/landing-page.module';
import { createCustomElement } from '@angular/elements';
import { IframeComponent } from './iframe/iframe.component';
import { registerLocaleData, DatePipe } from '@angular/common';
import localeFr from '@angular/common/locales/fr';
import localeDe from '@angular/common/locales/de';
import localeIt from '@angular/common/locales/it';
import localeNl from '@angular/common/locales/nl';
import localeEn from '@angular/common/locales/en';
import localeEs from '@angular/common/locales/es';
import localeCs from '@angular/common/locales/cs';
import localeSk from '@angular/common/locales/sk';
import localeDa from '@angular/common/locales/da';
import localePl from '@angular/common/locales/pl';
import localeEnExtra from '@angular/common/locales/extra/en';
import { HttpClientModule, HttpClient } from '@angular/common/http';
import { BrowserAnimationsModule } from '@angular/platform-browser/animations';
import { TranslateModule, TranslateLoader, TranslateService } from '@ngx-translate/core';
import { createTranslateLoader, TranslateConfig } from 'src/app/app.module';
import { SsrCookieService } from 'ngx-cookie-service-ssr';
import { RouterModule, Routes, UrlSerializer } from '@angular/router';
import { SearchWidgetComponent } from './search-widget/search-widget.component';
import { environment } from '../environments/environment';
import { PlatformManagerModule, PlatformInformationService } from 'projects/platform-manager/src/public_api';
import { LocalizeRouterModule, LocalizeParser, LocalizeRouterSettings } from '@gilsdav/ngx-translate-router';
import { Myserializer } from '../../../../src/app/shared/services/myserializer';
import { LocalizeUniversalLoader } from '../../../../src/app/shared/classes/localize-universal-loader';
import { APP_BASE_HREF } from '@angular/common';

export const config = {
  production: environment.production,
  env: environment.env,
  hostname: environment.hostname,
  language: environment.language,
  api_endpoints: environment.api.url,
  platform: environment.platform,
  platformFallback: environment.platformFallback,
  asset_path: environment.language_path,
  ekomi_widget: environment.ekomi_widget
};

// reduce overhead, no lazy loaded module routes for route generation
export const routes: Routes = [];

@NgModule({
  declarations: [IframeComponent, SearchWidgetComponent],
  imports: [
    PlatformManagerModule.forRoot(config),
    BrowserModule,
    LandingPageModule,
    HttpClientModule,
    BrowserAnimationsModule,
    TranslateModule.forRoot({
      loader: {
        provide: TranslateLoader,
        useFactory: createTranslateLoader,
        deps: [HttpClient, TransferState, PLATFORM_ID, TranslateConfig, Location]
      }
    }),
    RouterModule.forRoot(routes),
    LocalizeRouterModule.forRoot(routes, {
      parser: {
        provide: LocalizeParser,
        useFactory: (translate, location, settings, http) =>
          new LocalizeUniversalLoader(translate, location, settings, http, [
            'de',
            'en',
            'fr',
            'it',
            'nl',
            'sk',
            'cs',
            'pl',
            'es',
            'da'
          ]),
        deps: [TranslateService, Location, LocalizeRouterSettings, PlatformInformationService]
      },
      defaultLangFunction: (languages: Array<string>, cachedLang?: string, browserLang?: string) => {
        const params = new URLSearchParams(window.location.search);
        const urlParamLang = params.get('lang');

        if (urlParamLang) {
          return urlParamLang;
        }
        if (browserLang) {
          return browserLang;
        }
        if (cachedLang) {
          return cachedLang;
        }
        return languages[0];
      }
    })
  ],
  providers: [
    SsrCookieService,
    DatePipe,
    {
      provide: APP_BASE_HREF,
      useValue: document.getElementsByTagName('app-iframe').length
        ? '/components/skihire-iframe/'
        : 'https://www.alpinresorts.com/'
    },
    { provide: UrlSerializer, useClass: Myserializer },
    {
      provide: TranslateConfig,
      useValue: { path_server: environment.language_path_server, path_client: environment.language_path }
    }
  ],
  bootstrap: []
})
export class AppModule implements DoBootstrap {
  constructor(injector: Injector) {
    registerLocaleData(localeDe, 'de');
    registerLocaleData(localeFr, 'fr');
    registerLocaleData(localeIt, 'it');
    registerLocaleData(localeNl, 'nl');
    registerLocaleData(localeEs, 'es');
    registerLocaleData(localeCs, 'cs');
    registerLocaleData(localeSk, 'sk');
    registerLocaleData(localeDa, 'da');
    registerLocaleData(localePl, 'pl');
    registerLocaleData(localeEn, 'en', localeEnExtra);

    const el = createCustomElement(SearchWidgetComponent, { injector });
    customElements.define('skihire-search-widget', el);
  }

  /**
   * Bootstrap imperatively as the webcomponent shall not set up a bootstrapped component
   * @param appRef the application reference
   */
  ngDoBootstrap(appRef: ApplicationRef) {
    if (document.getElementsByTagName('app-iframe').length) {
      appRef.bootstrap(IframeComponent);
    }
  }
}
